// The "users on this switch" dialog — the React port of the PHP search
// page's #switchModal. Lists the subscribers configured behind one switch
// (from radius_subscribers, with live PPPoE state + cached ONU rx_power
// merged in by the Go side), with a status-filter pill row, a text
// filter, and the switch box's own ONU summary. A row links to the
// subscriber detail page; closed subscribers get a "→ Close box" action
// (move them off this switch onto the Permanent-Close switch). The
// footer's "Get RX Power" does a fresh SNMP sweep of these subscribers'
// ONUs and overlays the live readings.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, MoveRight, Wifi } from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@noc/ui";
import type { SwitchUserRow } from "@noc/api";
import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { accountStatusVariant, subscriberState, stateDotClass } from "@/lib/subscriberState";
import { rxPowerToneClass } from "@/features/subscribers/format";
import { OnuCard } from "@/features/subscribers/OnuCard";
import { useCan, useCanOLT } from "@/features/auth/roles";
import { useSwitchUsers } from "./useSwitches";

type StatusFilter = "all" | "online" | "blocked" | "offline";
const STATUS_FILTERS: StatusFilter[] = ["all", "online", "blocked", "offline"];

export function SwitchUsersDialog({
  switchName,
  onClose,
}: {
  switchName: string | null;
  onClose: () => void;
}) {
  const open = switchName != null;
  const qc = useQueryClient();
  const canWrite = useCan("subscribers:write");
  const { data, isPending, isError, error } = useSwitchUsers(switchName);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [text, setText] = useState("");
  const [rxOverride, setRxOverride] = useState<Record<string, string>>({});
  const [closeTarget, setCloseTarget] = useState<string | null>(null);

  const rows = useMemo<SwitchUserRow[]>(() => (data?.ok ? (data.rows ?? []) : []), [data]);

  const counts = useMemo(() => {
    const c = { all: rows.length, online: 0, blocked: 0, offline: 0 };
    for (const r of rows) {
      const st = subscriberState(r.status, r.ip_address);
      if (st === "online") c.online++;
      else if (st === "blocked") c.blocked++;
      else c.offline++;
    }
    return c;
  }, [rows]);

  const q = text.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    const st = subscriberState(r.status, r.ip_address);
    if (statusFilter === "online" && st !== "online") return false;
    if (statusFilter === "blocked" && st !== "blocked") return false;
    if (statusFilter === "offline" && (st === "online" || st === "blocked")) return false;
    if (q && !`${r.username} ${r.client_name ?? ""} ${r.mobile ?? ""} ${r.ip_address}`.toLowerCase().includes(q))
      return false;
    return true;
  });

  const onu =
    data?.ok && data.onu && typeof data.onu === "object" ? (data.onu as Record<string, unknown>) : null;

  // ── Fresh RX-power sweep of these subscribers' ONUs ──
  const rxM = useMutation({
    mutationFn: () => api.cmd.onuRx(rows.map((r) => r.username)),
    onSuccess: (r) => {
      if (r.ok) {
        const got = r.powers ?? {};
        setRxOverride((prev) => ({ ...prev, ...got }));
        toast.success(`RX power refreshed (${Object.keys(got).length}/${rows.length}).`);
        if (switchName) void qc.invalidateQueries({ queryKey: qk.switches.users(switchName) });
      } else {
        toast.error(`RX sweep failed: ${r.error ?? "unknown error"}`);
      }
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "RX sweep failed"),
  });

  // ── Move a closed subscriber to the "Permanent Close" switch ──
  const closeM = useMutation({
    mutationFn: async (username: string) => {
      const res = await api.radiusStatus.updateClient({ username, switch_name: "Permanent Close" });
      if (!res.ok || !res.result?.ok) {
        throw new Error(res.error ?? res.result?.portal_message ?? "portal rejected the change");
      }
      // Re-sync the local mirror so this list reflects the move immediately
      // (best-effort — the 3-min radiussync would catch it eventually).
      await api.radius.live(username).catch(() => undefined);
      return username;
    },
    onSuccess: (username) => {
      toast.success(`${username} moved to "Permanent Close".`);
      setCloseTarget(null);
      if (switchName) void qc.invalidateQueries({ queryKey: qk.switches.users(switchName) });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "move failed"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Flex column: pinned header + footer, the rest scrolls — keeps the
          dialog inside the viewport however many users / ONU fields. */}
      <DialogContent className="flex max-h-[88vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-6 pb-3 pt-5">
          <DialogTitle className="pr-8">
            Users on <span className="text-primary">{switchName}</span>
            {!isPending && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {counts.online}/{counts.all} online
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Subscribers configured behind this switch, with live connection state and the last known ONU
            optical reading.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
        {isError && (
          <Alert variant="destructive">
            <AlertDescription>{error instanceof Error ? error.message : "Lookup failed."}</AlertDescription>
          </Alert>
        )}

        {onu && switchName && <SwitchOnuCard switchName={switchName} cached={onu} />}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-xs font-medium capitalize transition-colors",
                  statusFilter === f
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                {f}
                {f !== "all" && <span className="ml-1 opacity-70">({counts[f]})</span>}
              </button>
            ))}
          </div>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Filter…"
            className="h-8 sm:ml-auto sm:w-48"
          />
        </div>

        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>RX</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    {rows.length === 0 ? "No subscribers on this switch." : "No users match the filter."}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => (
                  <UserRow
                    key={r.username}
                    r={r}
                    rxLive={rxOverride[r.username]}
                    canWrite={canWrite}
                    sendingClose={closeM.isPending && closeTarget === r.username}
                    onPick={onClose}
                    onSendClose={() => setCloseTarget(r.username)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
        </div>

        <DialogFooter className="border-t px-6 py-3 sm:justify-start">
          <Button
            variant="outline"
            size="sm"
            onClick={() => rxM.mutate()}
            disabled={rxM.isPending || rows.length === 0}
            title="Fresh SNMP read of every listed subscriber's ONU"
          >
            {rxM.isPending ? <Spinner /> : <Wifi />} Get RX power
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Confirm: move a closed subscriber to the Permanent-Close switch. */}
      <Dialog open={closeTarget != null} onOpenChange={(o) => !o && !closeM.isPending && setCloseTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send “{closeTarget}” to “Permanent Close”?</DialogTitle>
            <DialogDescription>
              Moves this closed subscriber off <span className="font-mono">{switchName}</span> onto the
              Permanent-Close switch, so this list only shows your active/blocked subscribers. Done via
              MyBilling — can be reversed by editing the subscriber's switch back.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCloseTarget(null)} disabled={closeM.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => closeTarget && closeM.mutate(closeTarget)} disabled={closeM.isPending}>
              {closeM.isPending && <Spinner />} Send to Permanent Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// The switch box's own uplink ONU — the cached `onu:<switch-hyphenated>`
// snapshot (which the Go side returns inline) overlaid with a fresh
// over-SSH read (the box's ONU desc is the switch name with spaces →
// dashes — the PHP modal does the same `onu_detail` merge). Reuses the
// subscriber card's <OnuCard> in read-only mode (no bind action).
function SwitchOnuCard({ switchName, cached }: { switchName: string; cached: Record<string, unknown> }) {
  // The switch box isn't a `radius_subscribers` row, so AssertOwnsSubscriber
  // would 403 a reseller — pass the box's OLT so the server can authorise
  // it via the OLT grant instead. And only attempt the live read when the
  // caller may act on that OLT (admin/support: always; reseller: iff
  // granted), so a reseller without it just sees the cached snapshot rather
  // than a dead 403.
  const oltName = typeof cached.olt === "string" ? cached.olt : "";
  const canRead = useCanOLT(oltName || null);
  const detailQ = useQuery({
    queryKey: qk.switches.onuDetail(switchName),
    queryFn: () => api.cmd.onuDetail(switchName.replace(/ /g, "-"), oltName || undefined),
    staleTime: 30_000,
    enabled: canRead,
  });
  const merged =
    detailQ.data?.ok && detailQ.data.onu && typeof detailQ.data.onu === "object"
      ? { ...cached, ...detailQ.data.onu }
      : cached;
  return <OnuCard onu={merged} busy={detailQ.isFetching} title="Uplink ONU" compact collapsible />;
}

function UserRow({
  r,
  rxLive,
  canWrite,
  sendingClose,
  onPick,
  onSendClose,
}: {
  r: SwitchUserRow;
  rxLive?: string;
  canWrite: boolean;
  sendingClose: boolean;
  onPick: () => void;
  onSendClose: () => void;
}) {
  const st = subscriberState(r.status, r.ip_address);
  const to = `/subscribers/${encodeURIComponent(r.username)}`;
  const isClosed = (r.account_status ?? "").toLowerCase() === "close";
  const rx = rxLive || r.rx_power_dbm || "";
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className={cn("inline-block size-2 shrink-0 rounded-full", stateDotClass(st))} />
          <Link to={to} onClick={onPick} className="font-mono font-medium hover:underline">
            {r.username}
          </Link>
        </div>
        {r.client_name && <div className="mt-0.5 text-xs text-muted-foreground">{r.client_name}</div>}
      </TableCell>
      <TableCell className="font-mono text-xs">{r.ip_address || "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Badge variant={accountStatusVariant(r.account_status)}>{r.account_status || "—"}</Badge>
          {isClosed && canWrite && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5 text-[11px] text-muted-foreground hover:text-destructive"
              onClick={onSendClose}
              disabled={sendingClose}
              title='Move to the "Permanent Close" switch'
            >
              {sendingClose ? <Spinner /> : <MoveRight className="size-3" />} Close box
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell className={cn("font-mono text-xs", rxPowerToneClass(rx), rxLive && "font-semibold")} title={rxLive ? "freshly polled" : undefined}>
        {rx || "—"}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs">{r.expires_on || "—"}</TableCell>
      <TableCell>
        <Link
          to={to}
          onClick={onPick}
          aria-label={`Open ${r.username}`}
          className="text-muted-foreground hover:text-foreground"
        >
          <ExternalLink className="size-3.5" />
        </Link>
      </TableCell>
    </TableRow>
  );
}
